import { Link } from "react-router-dom";
import { CalendarDays, ChevronRight, MapPin } from "lucide-react";
import { StatusBadge } from "@/components/common/StatusBadge";
import { AvatarStack } from "@/components/common/UserAvatar";
import { fmtDate, fmtMinutes } from "@/lib/format";
import { cn } from "@/lib/utils";

const relativeDay = (iso) => {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(`${iso}T00:00:00`);
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "I dag";
  if (diff === 1) return "I morgon";
  if (diff === -1) return "I går";
  return null;
};

/** Ordrekort for lister på mobil/nettbrett. Éi rad med tydeleg hierarki: nummer → tittel → kunde → meta. */
export const OrderCard = ({ order, showInvoice = false, className, testId = "order-card" }) => {
  const totalMinutes = (order.stats?.minutes || 0) + (order.stats?.resource_minutes || 0);
  const rel = relativeDay(order.planned_date);
  const place = order.logistics?.delivery?.name || order.site_address;
  return (
    <Link
      to={`/ordrar/${order.id}`}
      data-testid={testId}
      className={cn(
        "focus-ring group block min-w-0 rounded-lg border border-border bg-card px-4 py-3 transition-colors duration-150 hover:border-brand-grey active:bg-muted",
        className,
      )}
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className="num font-medium">{order.order_number}</span>
            <span aria-hidden="true">·</span>
            <span className="truncate">{order.template_label}</span>
          </p>
          <p className="mt-0.5 line-clamp-2 text-[15px] font-semibold leading-snug" data-testid={`${testId}-title`}>{order.title}</p>
          <p className="truncate text-sm text-muted-foreground">{order.customer_name}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <StatusBadge status={order.status} data-testid={`${testId}-status`} />
          {showInvoice && order.invoice_status && order.invoice_status !== "ingen" ? <StatusBadge kind="invoice" status={order.invoice_status} /> : null}
        </div>
      </div>
      <div className="mt-2.5 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5" />
          <span className="tabular">{fmtDate(order.planned_date)}</span>
          {rel ? <span className="rounded bg-secondary px-1 py-px text-[10px] font-semibold text-foreground">{rel}</span> : null}
        </span>
        {place ? (
          <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{place}</span>
          </span>
        ) : null}
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-border pt-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <AvatarStack users={order.assigned_users || []} testId={`${testId}-assigned`} />
          <span className="truncate text-xs text-muted-foreground">{order.assigned_users?.map((u) => u.name.split(" ")[0]).join(", ") || "Ingen tildelte"}</span>
        </div>
        <span className="inline-flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          <span className="tabular">{totalMinutes ? fmtMinutes(totalMinutes) : "0 min"}</span>
          <span className="tabular">{order.attachments_count} vedl.</span>
          <ChevronRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  );
};
